import { action, computed, observable } from "mobx";
import { message } from "antd";
import { SyntheticEvent } from "react";
import { ResizeCallbackData } from "react-resizable";
import { parseZone } from "moment";
import { Column } from "../components/common/Table/Interfaces";
import {
  COLUMNS_POSITION_CHANGED,
  USER_HAS_AUTHORIZED,
  USER_LOADED,
} from "../constants/events";
import { Setting } from "../models/Setting";
import ee from "../services/EventEmitter";
import { Option } from "../components/common/forms/Interfaces";
import { Department } from "../models/Department";
import { DEFAULT_ORDER } from "../constants";
import { NOT_DRAGGABLE_CLASS_NAME } from "../constants/table";
import ApiManager from "../mixins/ApiManager";
import { DepartmentHandOver } from "../models/DepartmentHandOver";
import { DepartmentHandOverResponse } from "../models/Responses/DepartmentHandOverResponse";
import { User } from "../models/Users/User";
import { getDefaultWidths, saveColumnWidth } from "../helpers/column";

export default class SettingsStore extends ApiManager {
  @observable
  isEnabledScrollbar = true;

  @observable
  isEnabledReorder = false;

  private _scrollPosition = 0;

  @computed
  get scrollPosition(): number {
    return this._scrollPosition;
  }

  set tableScrollPosition(value: number) {
    if (value !== 0) {
      this._scrollPosition = value;
    }
  }

  resetScrollPosition(): void {
    this._scrollPosition = 0;
  }

  settings: Setting[] = [];

  @observable
  private _clients: Option[] = [];

  @observable
  private _clientsWithNoClients: Option[] = [];

  @observable
  private _clientsWithRates: Option[] = [];

  @observable
  departmentHandOvers: DepartmentHandOver[] = [];

  @computed
  get noClient(): Option | undefined {
    return this._clients[0];
  }

  @computed
  get clients(): Option[] {
    return this._clients;
  }

  @computed
  get clientsWithNoClients(): Option[] {
    return this._clientsWithNoClients;
  }

  @computed
  get clientsWithRates(): Option[] {
    return this._clientsWithRates;
  }

  @observable
  private _creators: Option[] = [];

  @computed
  get creators(): Option[] {
    return this._creators;
  }

  @observable
  private _departments: Option[] = [];

  @computed
  get departments(): Option[] {
    return this._departments;
  }

  @observable
  columns: Column[] = [];

  @computed
  get activeColumns(): Column[] {
    const copy = this.getActiveColumns();

    if (this.isEnabledReorder) {
      return copy;
    }

    return copy.map((column, index) => {
      return {
        ...column,
        onHeaderCell: (item: Column) => ({
          width: item.width,
          onResize: this.handleResize(index),
        }),
      };
    });
  }

  private activeSettingsKey: string | null = null;

  private orderSettingsKey: string | null = null;

  constructor() {
    super();

    ee.on(USER_LOADED, (user) => {
      this.settings = (user as User).settings || [];
    });

    ee.on(USER_HAS_AUTHORIZED, () => {
      this.loadExtraData();
    });

    ee.on(COLUMNS_POSITION_CHANGED, (keys) => {
      this.saveColumnsPositions(keys as string[]);
    });
  }

  @action
  saveDeclarantActiveSettings(): void {
    if (!this.activeSettingsKey) {
      return;
    }

    const value: string[] = this.columns.reduce((total, amount) => {
      if (!amount.isActive) {
        return total;
      }

      return [...total, amount.key];
    }, [] as string[]);

    this.saveSettingsByKey(this.activeSettingsKey, value);
  }

  @action
  private saveSettingsByKey(name: string, values: unknown[]): void {
    this.apiManager
      .saveSettings({
        name,
        value: values,
      })
      .then(async (response) => {
        const data = await response.json();

        if (!response.ok) {
          message.error(data.message);
          return;
        }

        if (data.settings) {
          this.settings = data.settings;
        }

        message.success(data.message);
      })
      .finally(() => {
        this.setColumns(
          [...this.columns],
          this.orderSettingsKey as string,
          this.activeSettingsKey as string
        );
        ee.emit("table:re-render", null);
      });
  }

  @action
  toggleColumnByKey(keys: string[]): void {
    this.columns = [...this.columns].map((item) => ({
      ...item,
      isActive: keys.includes(item.key),
    }));
  }

  saveColumnsPositions(columns: string[]): void {
    if (!this.orderSettingsKey) {
      return;
    }

    this.saveSettingsByKey(this.orderSettingsKey, columns);
  }

  getSettingByName(key: string): [] {
    if (!Array.isArray(this.settings)) {
      return [];
    }

    const index = this.settings.findIndex((item) => {
      return item.name === key;
    });

    if (index >= 0 && this.settings[index]) {
      if (Array.isArray(this.settings[index].value)) {
        return this.settings[index].value as [];
      }

      return [];
    }

    return [];
  }

  static mapUsersToOptions(users: User[]): Option[] {
    return users.map((user) => ({
      title: user.nickname,
      value: user.id as string,
      extra: user.name,
    }));
  }

  @action
  loadClients(): void {
    if (this._clients.length === 0) {
      // Get Clients
      this.apiManager.getClients().then(async (response) => {
        if (!response.ok) return;
        this._clients = SettingsStore.mapUsersToOptions(
          (await response.json()) as User[]
        );
      });
    }

    if (this._clientsWithNoClients.length === 0) {
      // Get Clients with no clients group
      this.apiManager.getClientsWithNoClients().then(async (response) => {
        if (!response.ok) return;
        this._clientsWithNoClients = SettingsStore.mapUsersToOptions(
          (await response.json()) as User[]
        );
      });
    }

    if (this._clientsWithRates.length === 0) {
      // Get Clients with Rates
      this.apiManager.getClientsWithRates().then(async (response) => {
        if (!response.ok) return;
        this._clientsWithRates = SettingsStore.mapUsersToOptions(
          (await response.json()) as User[]
        );
      });
    }
  }

  @action
  loadDepartments(): void {
    if (this._departments.length > 0) {
      return;
    }

    this.apiManager.getDepartments().then(async (response) => {
      if (!response.ok) {
        return;
      }

      const data = (await response.json()) as Department[];

      this._departments = data.map((department) => ({
        title: department.name,
        value: department.id,
      }));
    });
  }

  @action
  loadCreators(): void {
    if (this._creators.length > 0) {
      return;
    }

    this.apiManager.getCreators().then(async (response) => {
      if (!response.ok) {
        return;
      }

      const data = (await response.json()) as User[];

      this._creators = data.map((user) => ({
        title: user.nickname,
        value: user.name,
        extra: user.id as string,
      }));
    });
  }

  setColumns<DataType>(
    columns: Column<DataType>[],
    orderColumnsKey: string,
    activeColumnsKey: string
  ): void {
    this.activeSettingsKey = activeColumnsKey;
    this.orderSettingsKey = orderColumnsKey;

    const copy = this.syncActiveColumns(columns, activeColumnsKey);
    const config = this.getSettingByName(orderColumnsKey);

    if (config.length === 0) {
      this.columns = [...copy] as Column[];
      return;
    }

    this.columns = [
      ...SettingsStore.sortColumnsByOrderConfig(copy as Column[], config),
    ];
    ee.emit("table:re-render", null);
  }

  syncActiveColumns<DataType>(
    columns: Column<DataType>[],
    settingKey: string
  ): Column<DataType>[] {
    const config = this.getSettingByName(settingKey) as string[];

    if (config.length === 0) {
      return columns;
    }

    const widths = this.activeSettingsKey
      ? getDefaultWidths(this.activeSettingsKey)
      : {};

    return columns.map((column) => ({
      ...column,
      isActive: config.includes(column.dataIndex),
      width: widths[column.key] || column.width,
    }));
  }

  static sortColumnsByOrderConfig(
    columns: Column[],
    config: string[]
  ): Column[] {
    return columns
      .map((column) => {
        const index = config.findIndex((key) => column.key === key);

        if (index >= 0) {
          return {
            ...column,
            order: index,
          };
        }

        return {
          ...column,
          order: DEFAULT_ORDER,
        };
      })
      .sort((first, second) => {
        if (first.order > second.order) {
          return 1;
        }

        if (first.order < second.order) {
          return -1;
        }

        return 0;
      });
  }

  onColumnDragEndHandler(from: number, to: number, ignore = 0): void {
    if (to < 0) return;

    const ignoredCount = this.getIgnoredColumns().length - ignore;

    const items = [...this.columns];
    const activeColumns = items.filter((column) => column.isActive);

    const toIndex = items.findIndex(
      (column) => column.key === activeColumns[to + ignoredCount].key
    );
    const fromIndex = items.findIndex(
      (column) => column.key === activeColumns[from + ignoredCount].key
    );

    const column = items.splice(fromIndex, 1)[0];
    items.splice(toIndex, 0, column);

    this.columns = items;

    this.saveColumnsPositions(items.map((item) => item.key) as string[]);
  }

  handleResize(
    index: number
  ): (e: SyntheticEvent, data: ResizeCallbackData) => void {
    return (e: SyntheticEvent, data: ResizeCallbackData) => {
      const columnKey = this.getActiveColumns()[index].key;

      const columnIndex = this.columns.findIndex(
        (column) => column.key === columnKey
      );

      const nextColumns = [...this.columns];
      nextColumns[columnIndex] = {
        ...nextColumns[columnIndex],
        width: data.size.width,
      };

      this.columns = nextColumns;

      if (this.activeSettingsKey) {
        saveColumnWidth(this.activeSettingsKey, columnKey, data.size.width);
      }
    };
  }

  private getActiveColumns(): Column[] {
    return this.columns.filter((column) => column.isActive);
  }

  private getIgnoredColumns(): Column[] {
    return this.columns.filter(
      (column) => column.className === NOT_DRAGGABLE_CLASS_NAME
    );
  }

  private loadExtraData() {
    this.loadClients();
    this.loadCreators();
    this.loadDepartments();
    this.loadDepartmentsHandOver();
  }

  private loadDepartmentsHandOver() {
    this.apiManager.getLatestDepartmentDates().then(async (response) => {
      if (!response.ok) {
        return;
      }

      const data: DepartmentHandOverResponse[] = await response.json();

      this.departmentHandOvers = data.map((item) => ({
        department_id: item.department_id,
        created_at: parseZone(item.created_at.toString()),
      }));
    });
  }
}
